import { useEffect, useState } from 'react';

import { useIntl } from 'react-intl';
import { Alert, Button, Col, Form, Row } from 'react-bootstrap';
import { Field, useFormikContext } from 'formik';

import { pin } from 'assets';
import { HomeIcon, PinIcon, WarningOrangeIcon } from 'assets/icons';
import { Input } from 'components/Forms';
import { Address } from 'components/UI';
import { InviteType } from 'constants/enums';
import { addressesAreEqual, isAddressValid } from 'utils/Address';

import { IShippingInfo } from '../Onboard';

interface IShippingAddressFormProps {
  gid: string;
  inviteType?: InviteType;
  showAddressToggle?: boolean;
  homeShippingCandidate?: IShippingInfo;
  lastShippingCandidate?: IShippingInfo;
}

const ShippingAddressForm = ({
  gid,
  inviteType,
  showAddressToggle = false,
  homeShippingCandidate,
  lastShippingCandidate
}: IShippingAddressFormProps) => {
  const intl = useIntl();
  const { values, setFieldValue } = useFormikContext<{ shippingInfo: IShippingInfo }>();
  const [addressSource, setAddressSource] = useState<'shipping' | 'home'>('shipping');
  const [addressKey, setAddressKey] = useState(0);

  const applyShippingCandidate = (candidate: IShippingInfo, source: 'shipping' | 'home') => {
    setFieldValue('shippingInfo.firstName', candidate.firstName);
    setFieldValue('shippingInfo.lastName', candidate.lastName);
    setFieldValue('shippingInfo.email', candidate.email);
    setFieldValue('shippingInfo.phoneNumber', candidate.phoneNumber);
    setFieldValue('shippingInfo.street_address_line_2', candidate.street_address_line_2 || '');
    setFieldValue('shippingInfo.address', candidate.address);
    setAddressSource(source);
    // Remount the Address block so the autocomplete also drops its geocoder pick and touched state.
    setAddressKey((key) => key + 1);
  };

  const handleSelectAddress = (source: 'shipping' | 'home') => {
    if (source === addressSource) return;
    const candidate = source === 'home' ? homeShippingCandidate : lastShippingCandidate;
    if (candidate) applyShippingCandidate(candidate, source);
  };

  // "Use as patient address" is redundant when the shipping address already matches the home address.
  const patientHomeAddress = homeShippingCandidate?.address;
  const shippingMatchesHomeAddress =
    !!isAddressValid(patientHomeAddress) &&
    addressesAreEqual(values.shippingInfo.address, patientHomeAddress);

  const showUseAsPatientAddress =
    !shippingMatchesHomeAddress &&
    inviteType !== InviteType.BULK_ORDER_DEVICES &&
    inviteType !== InviteType.BULK_ORDER_ACCESSORIES;
  const showMismatchWarning =
    !!isAddressValid(patientHomeAddress) &&
    !addressesAreEqual(values.shippingInfo.address, patientHomeAddress);

  useEffect(() => {
    if (shippingMatchesHomeAddress && values.shippingInfo.useAsPatientAddress) {
      setFieldValue('shippingInfo.useAsPatientAddress', false);
    }
  }, [shippingMatchesHomeAddress, values.shippingInfo.useAsPatientAddress, setFieldValue]);

  const useAsPatientAddressField = (
    <Field
      type="checkbox"
      name="shippingInfo.useAsPatientAddress"
      id="shippingInfo.useAsPatientAddress"
      label={intl.formatMessage({ id: 'useShippingAsPatientAddressKey' })}
      as={Form.Check}
      data-test="shipping-info-use-as-patient-address"
    />
  );

  return (
    <>
      <h6 className="mt-5 mb-4">
        <img src={pin} className="me-2" />
        {intl.formatMessage({ id: 'shippingAddressKey' }).toUpperCase()}
      </h6>
      {showAddressToggle && (
        <div className="address-source-toggle d-flex flex-wrap gap-2 mb-3">
          <Button
            variant={addressSource === 'home' ? 'info' : 'outline-info'}
            onClick={() => handleSelectAddress('home')}
            data-test="shipping-address-home-btn"
          >
            <HomeIcon className="icon me-2 align-middle" style={{ fontSize: '18px' }} />
            <span className="align-middle">
              {intl.formatMessage({ id: 'patientHomeAddressKey' })}
            </span>
          </Button>
          <Button
            variant={addressSource === 'shipping' ? 'info' : 'outline-info'}
            onClick={() => handleSelectAddress('shipping')}
            data-test="shipping-address-last-shipping-btn"
          >
            <PinIcon className="icon me-2 align-middle" style={{ fontSize: '18px' }} />
            <span className="align-middle">
              {intl.formatMessage({ id: 'lastShippingAddressKey' })}
            </span>
          </Button>
        </div>
      )}
      {showMismatchWarning && (
        <Alert variant="warning" className="mb-4" data-test="shipping-address-mismatch-warning">
          <div>
            <WarningOrangeIcon className="icon me-2 align-middle" style={{ fontSize: '18px' }} />
            <span className="align-middle">
              {intl.formatMessage({ id: 'shippingAddressDiffersWarningKey' })}
            </span>
          </div>
          {showUseAsPatientAddress && <div className="mt-3">{useAsPatientAddressField}</div>}
        </Alert>
      )}
      <Row>
        {showUseAsPatientAddress && !showMismatchWarning && (
          <Form.Group className="form-group mb-3" as={Col} sm={12}>
            {useAsPatientAddressField}
          </Form.Group>
        )}
        <Form.Group className="form-group" as={Col} md="6">
          <Input
            type="text"
            placeholder={intl.formatMessage({ id: 'firstName' })}
            label={intl.formatMessage({ id: 'firstName' })}
            name="shippingInfo.firstName"
            required
          />
        </Form.Group>
        <Form.Group className="form-group" as={Col} md="6">
          <Input
            type="text"
            placeholder={intl.formatMessage({ id: 'lastName' })}
            label={intl.formatMessage({ id: 'lastName' })}
            name="shippingInfo.lastName"
            required
          />
        </Form.Group>
        <Form.Group className="form-group" as={Col} sm={12}>
          <Address
            key={addressKey}
            gid={gid}
            defaultAddress={values.shippingInfo.address}
            formSchema="shippingInfo.address"
          />
        </Form.Group>
      </Row>
      <h6 className="mb-4">{intl.formatMessage({ id: 'contactInfoShippingKey' }).toUpperCase()}</h6>
      <Row>
        <Form.Group className="form-group" as={Col} md="6">
          <Input
            type="phone"
            placeholder={intl.formatMessage({ id: 'phoneNumber' })}
            label={intl.formatMessage({ id: 'phoneNumber' })}
            name="shippingInfo.phoneNumber"
          />
        </Form.Group>
        <Form.Group className="form-group" as={Col} md="6">
          <Input
            type="email"
            placeholder={intl.formatMessage({ id: 'emailAddressKey' })}
            label={intl.formatMessage({ id: 'emailAddressKey' })}
            name="shippingInfo.email"
            required={
              inviteType === InviteType.BULK_ORDER_DEVICES ||
              inviteType === InviteType.BULK_ORDER_ACCESSORIES
            }
          />
        </Form.Group>
      </Row>
    </>
  );
};

export default ShippingAddressForm;
